import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Layers } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SpaceAvatar } from "@/components/Avatars";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const { data: inv, error } = useQuery({
    queryKey: ["invitation", token],
    queryFn: async () => (await api.get(`/invitations/${token}`)).data,
    retry: false,
  });

  const accept = async () => {
    try {
      const { data } = await api.post(`/invitations/${token}/accept`);
      toast.success("¡Bienvenido al Espacio!");
      navigate(`/space/${data.workspace_id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="max-w-md w-full p-8 text-center space-y-6 animate-fade-up" data-testid="invite-page">
        <div className="flex justify-center">
          <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
            <Layers className="h-5 w-5 text-primary-foreground" />
          </div>
        </div>
        {error ? (
          <>
            <p className="text-3xl">😕</p>
            <p className="font-heading font-semibold text-lg" data-testid="invite-error">{apiError(error)}</p>
            <Button variant="outline" onClick={() => navigate("/")}>Ir a Inicio</Button>
          </>
        ) : !inv ? (
          <p className="text-muted-foreground">Cargando invitación...</p>
        ) : (
          <>
            <div className="flex justify-center">
              <SpaceAvatar icon={inv.workspace_icon} color={inv.workspace_color} size="xl" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold" data-testid="invite-workspace-name">{inv.workspace_name}</h1>
              <p className="text-sm text-muted-foreground mt-2">
                <span className="font-semibold text-foreground">{inv.invited_by_name}</span> te invita a unirte como{" "}
                <span className="font-semibold text-foreground">{inv.role_label}</span>
              </p>
            </div>
            {loading ? null : user ? (
              <Button className="w-full" data-testid="invite-accept-btn" onClick={accept}>
                Aceptar invitación
              </Button>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Inicia sesión o crea una cuenta para unirte</p>
                <Button className="w-full" data-testid="invite-login-btn"
                  onClick={() => navigate(`/auth?redirect=/invite/${token}`)}>
                  Iniciar sesión / Registrarme
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
